// src/contexts/vendas/PDVContext.js
import React, { createContext, useContext, useState } from 'react';
import { Button } from 'react-bootstrap';
import Modal from '../../components/modalForm/StackedBootstrapModal';
import { NotaItem } from '../../models/vendas/NotaItem';
import { useCaixa } from '../caixas/CaixaContext';
import { useMessage } from '../geral/MessageContext';
import { formatNumeric, normalizeDecimalInputBr, parseCurrency, parseNumeric } from '../../utils/numberUtils';
import { useProduto } from '../produtos/ProdutoContext';
import Input from "../../components/input/Input";
import { calcularTotalItemPDV, criarItemPDVPorProduto } from '../../services/vendas/PDVService';

const PDVContext = createContext();

export const usePDV = () => useContext(PDVContext);

export const PDVProvider = ({ children }) => {
    const [itensPDV, setItensPDV] = useState([]);
    const [itemPDVSel, setItemPDVSel] = useState(new NotaItem());
    const [numItem, setNumItem] = useState(1);    
    const {caixaSel } = useCaixa();
    const {produtos} = useProduto();
    const [barCode, setBarCode] = useState("");
    const { showMessage } = useMessage();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [notaItemDelete, setNotaItemDelete] = useState();
    const [numeroItemDelete, setNumeroItemDelete] = useState('');


    const validarInclusao = () => {
        if (!caixaSel?.aberto) {
            showMessage("Caixa fechado! Não é possível adicionar produtos. Pressione 'Alt+C' ou clique no botão do caixa para realizar a abertura.", 'warning');
            return false;
        }
        if (itensPDV.length > 0) {
            if (parseCurrency(itemPDVSel.qtdCom) <= 0) {
                showMessage("Informe uma quantidade para o produto '" + itemPDVSel.xProd + "'.", 'warning');
                return false;
            }
            if (parseCurrency(itemPDVSel.vUnCom) <= 0) {
                showMessage("Informe um valor para o produto '" + itemPDVSel.xProd + "'.", 'warning');
                return false;
            }
        }

        return true;
    };

    const adicionarProdutos = (selecao) => {
        if (!selecao.length) return { itens: [], selecionado: null };
        if (!validarInclusao()) return false;

        const entradas = selecao.map(item => ({
            produto: produtos.find(produto => produto.id === item.produto.id),
            quantidade: item.quantidade,
        }));
        if (entradas.some(item => !item.produto || !Number.isSafeInteger(item.quantidade) || item.quantidade <= 0)) {
            showMessage('Não foi possível incluir a seleção. Confira os produtos e suas quantidades.', 'warning');
            return false;
        }

        const primeiroNumero = Math.max(numItem, ...itensPDV.map(item => Number(item.item) + 1));
        const novosItens = entradas.map((entrada, index) => criarItemPDVPorProduto({
            ...entrada, numItem: primeiroNumero + index,
        }));
        const selecionado = novosItens.find(item => parseCurrency(item.vUnCom) <= 0) || novosItens[novosItens.length - 1];
        setItensPDV(anteriores => [...anteriores, ...novosItens]);
        setNumItem(primeiroNumero + novosItens.length);
        setItemPDVSel(selecionado);
        setBarCode('');
        return { itens: novosItens, selecionado };
    };

    const addProduct = (code) => {
        if (!validarInclusao()) return null;
        const product = produtos.find(p => p.id.toString() === code.toString() || p.ean === code.toString());
        if (product) {
            const newItemPDV = criarItemPDVPorProduto({ produto: product, numItem });
            setNumItem(numItem + 1);

            setItensPDV(prev => [...prev, newItemPDV]);
            setItemPDVSel(newItemPDV);
            setBarCode("");

            return newItemPDV; // RETORNA o item criado
        } else {
            showMessage("Produto não encontrado!", "warning");
            return null;
        }
    };


    const atualizarCampoItem = (campo, valor, formatar = false) => {
        if (!itemPDVSel?.item || (campo === 'vUnCom' && itemPDVSel.produto?.precoFixo)) return;

        const texto = String(valor ?? '');
        const normalizado = normalizeDecimalInputBr(
            texto.includes(',') ? texto.replace(/\./g, '') : texto,
            campo === 'qtdCom' ? -1 : 2
        );
        const valorCampo = formatar ? formatNumeric(parseNumeric(normalizado)) : normalizado;
        const atualizarItem = (item) => {
            const atualizado = { ...item, [campo]: valorCampo };
            return {
                ...atualizado,
                vProd: calcularTotalItemPDV({
                    quantidade: parseNumeric(atualizado.qtdCom),
                    valorUnitario: parseNumeric(atualizado.vUnCom)
                })
            };
        };

        setItemPDVSel(atualizarItem);
        setItensPDV(prev => prev.map(item => item.item === itemPDVSel.item ? atualizarItem(item) : item));
    };

    const handleAjustaTotal = (e) => {
        const campo = e.target.id === 'qtde' ? 'qtdCom' : 'vUnCom';
        atualizarCampoItem(campo, e.target.value, campo === 'vUnCom');
    };

    const handleMudaDescricao = (e) => {
        setItemPDVSel(prev => ({ ...prev, xProd: e.target.value }));
        setItensPDV(prev => prev.map(item => item.item === itemPDVSel.item ? { ...item, xProd: e.target.value } : item));
    };

    const handleQuantidadeChange = (e) => {
        atualizarCampoItem('qtdCom', e.target.value);
    };

    const handleValorUnChange = (e) => {
        atualizarCampoItem('vUnCom', e.target.value);
    };  

    const handleDeleteItem = () => {
        if (notaItemDelete) {
            setItensPDV(prev => prev.filter(item => item.item !== notaItemDelete.item));
            setShowDeleteModal(false);
            setNotaItemDelete(null);
            setNumeroItemDelete('');
        }
    };

    const selectItem = (e) => {
        const numeroItem = e.target.value;
        const itemEncontrado = itensPDV.find(p => p.item.toString() === numeroItem.toString());

        setNumeroItemDelete(numeroItem);
        setNotaItemDelete(itemEncontrado || null);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setNotaItemDelete(null);
        setNumeroItemDelete('');
    };

    return (
        <PDVContext.Provider value={{ itensPDV, setItensPDV, itemPDVSel, setItemPDVSel, numItem, setNumItem, barCode, setBarCode, 
            addProduct, adicionarProdutos, showDeleteModal, setShowDeleteModal,
            handleAjustaTotal, handleMudaDescricao, handleQuantidadeChange, handleValorUnChange }}>            
            {children}

            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Excluir Item</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Input
                            label="Informe o número do item para excluir"
                            value={numeroItemDelete}
                            onChange={selectItem}
                            autoFocus
                        />
                        <Input
                            label="Descrição"
                            value={notaItemDelete?.produto?.descricao || ''}
                            size="100%"
                            disabled
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseDeleteModal}>
                            Cancelar
                        </Button>
                        <Button variant="danger" onClick={handleDeleteItem} disabled={!notaItemDelete}>
                            Excluir
                        </Button>
                    </Modal.Footer>
                </Modal>
        </PDVContext.Provider>
    );
};
